import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/Buttons";
import ModalComponent from "components/Modal/Orders";
import { useMutation } from "@apollo/client";
import { cancelOrder } from "apollo/mutations/cancelOrder";
import ModalStore from "components/Modal/ModalStore";
import advancedFormat from 'dayjs/plugin/advancedFormat'
import dayjs from "dayjs";
import { formatString } from "utils";
import CustomRequestDetailsModal from "./CustomRequestDetailsModal";

dayjs.extend(advancedFormat);

const SaddleTable = ({ activeTab, boughtSaddles, soldSaddles, customRequests = [] }) => {
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [CANCEL_ORDER, { loading }] = useMutation(cancelOrder);

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const handleOrderCancel = async (orderId) => {
    try {
      await CANCEL_ORDER({
        variables: {
          id: orderId,
        },
        refetchQueries: ['getUserTransactions']
      });
      setOpenModal(false);
      ModalStore?.openSimpleSuccess({
        icon: undefined,
        fontSize: "40px",
        title: "Your cancel request has been submitted successfully.",
      });
    } catch (error) {
      console.error(error?.message);
    }
  };

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const getStatusColor = (status) => {
    const s = status?.toLowerCase();
    if (s?.includes('pending')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    if (s?.includes('success') || s?.includes('shipped') || s?.includes('paid') || s?.includes('fulfilled')) return 'bg-green-100 text-green-800 border-green-200';
    if (s?.includes('cancel') || s?.includes('fail')) return 'bg-red-100 text-red-800 border-red-200';
    return 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const renderStandardRow = (saddle) => (
    <tr key={saddle.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="px-6 py-5 text-left">
        <button
          onClick={() => handleOrderClick(saddle.orderId)}
          className="order_id_link font-semibold"
        >
          #{saddle?.shopifyOrder || saddle?.orderId?.substring(0, 8) || "N/A"}
        </button>
      </td>
      <td className="px-6 py-5 text-left text-gray-600">
        {dayjs(saddle?.createdAt).format("MMM Do YYYY [at] h:mm A")}
      </td>

      <td className="px-6 py-5 text-left">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
          saddle?.orderStatus?.length
            ? saddle.orderStatus
              .slice()
              .sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))[0]?.status
            : "N/A"
        )}`}>
          {saddle?.orderStatus?.length
            ? formatString(
              saddle.orderStatus
                .slice()
                .sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))
                .map((status) => status?.status)[0]
            )
            : "N/A"}
        </span>
      </td>

      <td className="px-6 py-5 text-left">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(saddle?.paymentStatus)}`}>
          {formatString(saddle?.paymentStatus) || "N/A"}
        </span>
      </td>
      <td className="px-6 py-5">
        <div className="flex justify-center">
          <Button
            disabled={
              !!saddle?.isCancelRequestSent ||
              saddle?.shopifyFulfillmentStatus === "FULFILLED" ||
              saddle?.status === "PENDING"
            }
            onClick={() => {
              setSelectedOrderId(saddle.id);
              setOpenModal(true);
            }}
            className={`!rounded-lg !h-[36px] !px-4 !text-[12px] font-medium transition-all ${!!saddle?.isCancelRequestSent ||
              saddle?.shopifyFulfillmentStatus === "FULFILLED" ||
              saddle?.status === "PENDING"
              ? "opacity-50 cursor-not-allowed grayscale"
              : "bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
              }`}
          >
            Cancel Order
          </Button>
        </div>
      </td>
    </tr>
  );

  const renderCustomRow = (request) => (
    <tr key={request.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="px-6 py-5 text-left font-semibold text-[var(--color-primary)]">
        #{request.orderId}
      </td>
      <td className="px-6 py-5 text-left">
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{request.horseName}</span>
          <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{request.saddleType}</span>
        </div>
      </td>
      <td className="px-6 py-5 text-left text-gray-600">
        {dayjs.unix(Number(request.createdAt)).format("MMMM D, YYYY [at] h:mm A")}
      </td>
      <td className="px-6 py-5 text-left">
        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(request.status)}`}>
          {formatString(request.status)}
        </span>
      </td>
      <td className="px-6 py-5">
        <div className="flex justify-center">
          <button
            onClick={() => {
              setSelectedRequest(request);
              setIsDetailsModalOpen(true);
            }}
            className="view_details_btn transition-all inline-flex items-center gap-1"
          >
            View Details
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );

  const showNoOrders = (activeTab === 'standard' && !boughtSaddles.length && !soldSaddles.length) ||
    (activeTab === 'custom' && !customRequests.length);

  if (showNoOrders) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20 bg-white rounded-[32px] border border-dashed border-gray-300 mt-10">
        <div className="bg-gray-50 p-6 rounded-full mb-4">
          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-[var(--color-charcoal)] font-montserrat uppercase tracking-tight font-barlow">No {activeTab} orders found</h3>
        <p className="text-gray-500 mt-2 font-medium font-montserrat">You haven&apos;t made any {activeTab} orders yet.</p>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-gray-100 mt-10 overflow-hidden mb-20">
      <div className="overflow-x-auto no-scrollbar">
        <table className="w-full border-collapse min-w-[1000px]">
          <thead>
            <tr className="bg-[#F9FAFB] border-b border-gray-100 table_head ">
              {activeTab === 'standard' ? (
                <>
                  <th className="px-6 py-6 text-left">Order ID</th>
                  <th className="px-6 py-6 text-left">Date</th>
                  <th className="px-6 py-6 text-left">Status</th>
                  <th className="px-6 py-6 text-left">Payment</th>
                  <th className="px-6 py-6 text-center">Actions</th>
                </>
              ) : (
                <>
                  <th className="px-6 py-6 text-left">Request ID</th>
                  <th className="px-6 py-6 text-left">Horse & Type</th>
                  <th className="px-6 py-6 text-left">Date</th>
                  <th className="px-6 py-6 text-left">Status</th>
                  <th className="px-6 py-6 text-center">Actions</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white font-montserrat">
            {activeTab === 'standard' ? (
              <>
                {boughtSaddles.map((saddle) => renderStandardRow(saddle))}
                {soldSaddles.map((saddle) => renderStandardRow(saddle))}
              </>
            ) : (
              customRequests.map((request) => renderCustomRow(request))
            )}
          </tbody>
        </table>
      </div>

      <ModalComponent
        title="Cancel Order"
        modalIsOpen={openModal}
        setOpenModal={setOpenModal}
        loading={loading}
        onConfirm={() => handleOrderCancel(selectedOrderId)}
      >
        <p className="text-gray-600 text-center py-4 font-medium font-montserrat">
          Are you sure you want to cancel this order? This action cannot be undone.
        </p>
      </ModalComponent>

      <CustomRequestDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        request={selectedRequest}
      />
    </div>
  );
};

export default SaddleTable;
